import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "route-search" }
const _hoisted_2 = { class: "road-name-direction" }
const _hoisted_3 = { class: "buttons ib vm" }
const _hoisted_4 = ["onClick", "disabled"]
const _hoisted_5 = { class: "road-name ib vm mr4" }
const _hoisted_6 = ["onUpdate:modelValue", "onChange"]
const _hoisted_7 = ["value"]
const _hoisted_8 = { class: "direction ib vm" }
const _hoisted_9 = ["onUpdate:modelValue", "onChange"]
const _hoisted_10 = ["value"]
const _hoisted_11 = { class: "buttons ib vm ml1" }
const _hoisted_12 = { class: "arrow-wrap" }
const _hoisted_13 = {
  key: 0,
  class: "arrow fas fa-angle-down"
}
const _hoisted_14 = { class: "ta-center mt16 mb16" }
const _hoisted_15 = ["disabled"]

import type { RoadNameDisp } from 'src/models/master';
import {
  computed,
  onMounted,
  reactive,
  watch,
} from 'vue';
import type { GeoConnection } from 'src/models';
import type { GIKp } from 'src/models/geoItem';
import type { RouteSearchRouteEntry } from 'src/models/apis/movie/movieRequest';
import { isKpIncreaseDirection } from 'src/lib/kilopostHelper';
import { useReactiveMasters } from 'src/composables/useMasters';

interface RouteInfoItem {
  type: 'start' | 'end';
  roadNameDisp: string;
  roadName: string;
  direction: string;
  placeName: string;
  kpEnd?: number;
  kpStart?: number;
}

interface RouteInfo {
  routeInfo: RouteInfoItem[];
  dstRoadNameDisp: string;
  dstRoadName: string;
  dstDirection: string;
}

interface GetRouteInfoParams {
  k: string;
  obj: GeoConnection;
  srcRoadName: string;
  srcDirection: string;
}

type CanMapElement = RoadNameDisp & {routeInfoMap: {[k: string]: RouteInfoItem[] }};

type LocalRoute = {
  roadNameDisps: RoadNameDisp[];
  selectedRoadNameDispObj: CanMapElement | null;
  selectedDirection: string;
};

interface RouteSearchState {
  routes: LocalRoute[];
}

interface Props {
  roadNameDisps: RoadNameDisp[];
  isValidDateParams: boolean;
}

const maxLen = 3;
const routeMarginDistance = 2.0;

export default /*@__PURE__*/_defineComponent({
  __name: 'RouteSearch',
  props: {
    roadNameDisps: {},
    isValidDateParams: { type: Boolean }
  },
  emits: ['route-search'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const state = reactive<RouteSearchState>({
  routes: [],
});
const { masters: msts } = useReactiveMasters();

onMounted(() => {
  if (props.roadNameDisps.length > 0 && state.routes.length === 0) {
    initData();
  }
});

watch(() => props.roadNameDisps, () => {
  if (props.roadNameDisps.length > 0 && state.routes.length === 0) {
    initData();
  }
});

// computed
const canRouteSearch = computed(() => {
  if (!props.isValidDateParams) return false;
  if (state.routes.length < 2) return false;

  for (const route of state.routes) {
    if (!isRowFilled(route)) {
      return false;
    }
  }
  return true;
});

// methods
const initData = () => {
  const arr: LocalRoute[] = [];
  arr.push(getNewRow(props.roadNameDisps));
  arr.push(getNewRow([]));
  state.routes = arr;
};
const getNewRow = (candidacies: RoadNameDisp[]): LocalRoute => ({
  roadNameDisps: candidacies,
  selectedRoadNameDispObj: null,
  selectedDirection: '',
});
const isLast = (idx: number) => {
  return idx >= state.routes.length - 1;
};
const isRowFilled = (rowObj: LocalRoute) => {
  return !!rowObj.selectedRoadNameDispObj &&
    !!rowObj.selectedDirection;
};
const getRouteInfo = ({ k, obj, srcRoadName, srcDirection }: GetRouteInfoParams): RouteInfo => {
  let [dstRoadName, dstDirection, dstPlaceName] = k.split('#');
  const routeInfo: RouteInfoItem[] = [
    {
      type: 'end',
      roadNameDisp: msts.kp.roadNameToRoadNameDispMap[srcRoadName],
      roadName: srcRoadName,
      direction: srcDirection,
      placeName: 'main_line',
      kpEnd: obj.srcKpEnd,
    },
    {
      type: 'start',
      roadNameDisp: msts.kp.roadNameToRoadNameDispMap[dstRoadName],
      roadName: dstRoadName,
      direction: dstDirection,
      placeName: dstPlaceName || 'main_line',
      kpStart: obj.dstKpStart,
    },
  ];
  if (obj.nextKey) {
    const [viaRoadName, viaDirection, viaPlaceName] =
      [dstRoadName, dstDirection, dstPlaceName];
    const tmpSplit = obj.nextKey.split('#');
    dstRoadName = tmpSplit[0];
    dstDirection = tmpSplit[1];
    dstPlaceName = tmpSplit[2];
    if (dstPlaceName) {
      // 行き先が本線以外の場合というのはないとは思うが、あったらはじく
      return {} as RouteInfo;
    }
    const nestedObj = obj.nested[obj.nextKey];
    if (!nestedObj) return {} as RouteInfo;
    routeInfo.push(
      {
        type: 'end',
        roadNameDisp: msts.kp.roadNameToRoadNameDispMap[viaRoadName],
        roadName: viaRoadName,
        direction: viaDirection,
        placeName: viaPlaceName || 'main_line',
        kpEnd: nestedObj.srcKpEnd,
      },
      {
        type: 'start',
        roadNameDisp: msts.kp.roadNameToRoadNameDispMap[dstRoadName],
        roadName: dstRoadName,
        direction: dstDirection,
        placeName: dstPlaceName || 'main_line',
        kpStart: nestedObj.dstKpStart,
      },
    );
  }
  const dstRoadNameDisp = msts.kp.roadNameToRoadNameDispMap[dstRoadName];
  return { routeInfo, dstRoadNameDisp, dstRoadName, dstDirection };
};
const getNextCandidacies = (rowObj: LocalRoute) => {
  if (!isRowFilled(rowObj) || rowObj.selectedRoadNameDispObj === null) return [];

  // roadNameDispに含まれるroadNameごとに処理を行う
  const srcRoadNameDisp = rowObj.selectedRoadNameDispObj.roadNameDisp;
  const srcDirection = rowObj.selectedDirection;

  const candMap: { [k: string]: CanMapElement } = {};
  const srcRoadNames = msts.kp.roadNameDispMap[srcRoadNameDisp].roadNames;
  srcRoadNames.forEach(srcRoadName => {
    const connection = msts.kp.geoConnections[`${srcRoadName}#${srcDirection}`];
    if (!connection) return;

    // src情報からありうるdst情報を算出
    for (const [k, obj] of Object.entries(connection)) {
      const { routeInfo, dstRoadNameDisp, dstDirection } =
        getRouteInfo({ k, obj, srcRoadName, srcDirection });
      if (!routeInfo) { continue; }
      // 自分への接続は認めない
      if (
        srcRoadNameDisp === dstRoadNameDisp &&
        srcDirection === dstDirection
      ) { continue; }
      // dstRoadNameDispごとにまとめ直す
      // dstRoadNameDisp#dstDirectionではかぶらないはずだから、
      // それ単位であとで取れるようにrouteInfoを保存しておく
      if (!dstRoadNameDisp || !dstDirection) { continue; }
      if (!candMap[dstRoadNameDisp]) {
        candMap[dstRoadNameDisp] =
          JSON.parse(JSON.stringify(msts.kp.roadNameDispMap[dstRoadNameDisp]));
        candMap[dstRoadNameDisp].directions = [];
        candMap[dstRoadNameDisp].routeInfoMap = {};
      }
      const candMapEnt = candMap[dstRoadNameDisp];
      if (!candMapEnt.routeInfoMap[dstDirection]) {
        candMapEnt.directions.push(dstDirection);
      }
      candMapEnt.routeInfoMap[dstDirection] = routeInfo;
    }
  });
  const ret = Object.entries(candMap).reduce((acc: CanMapElement[], ent) => {
    acc.push(ent[1]); return acc;
  }, []);
  return ret;
};

const addNextRow = (currentRow: LocalRoute) => {
  if (state.routes.length >= maxLen || !isRowFilled(currentRow)) return;

  const cands = getNextCandidacies(currentRow);
  const row = getNewRow(cands);
  state.routes.push(row);
};
const deleteLastRow = () => {
  const idx = state.routes.length - 1;
  if (idx <= 0) return;

  state.routes.splice(idx, 1);
};
const onSelectionChange = (idx: number) => {
  // 後続の行があれば全て選択解除
  state.routes.slice(idx + 1).forEach(e => {
    e.roadNameDisps = [];
    e.selectedRoadNameDispObj = null;
    e.selectedDirection = '';
  });
  // 次の行があれば候補をセット
  if (idx >= state.routes.length - 1) return;

  const cands = getNextCandidacies(state.routes[idx]);
  state.routes[idx + 1].roadNameDisps = cands;
};
const onRoadNameDispChange = (idx: number) => {
  state.routes[idx].selectedDirection = '';
  onSelectionChange(idx);
};
const onDirectionChange = (idx: number) => {
  onSelectionChange(idx);
};
const kpBinSearch = (kps: GIKp[], num: number) => {
  // numを見つける. kpsは昇順前提.
  let low = 0;
  let high = kps.length - 1;
  let mid, guess;
  while (low <= high) {
    mid = Math.floor((low + high) / 2);
    guess = kps[mid];
    if (Math.abs(guess.kpCalc - num) < Number.EPSILON) {
      return mid;
    } else if (guess.kpCalc > num) {
      high = mid - 1;
    } else {
      low = mid + 1;
    }
  }
  return -1;
};
const findFirstKpOutsideDistance = (
  roadName: string,
  direction: string,
  kpCalc0: number,
  moveIndexFunc: (_: number) => number,
  distance: number,
): GIKp | null => {
  const kpMapKey = `${roadName}#${direction}`;
  const kps = msts.kp.kpMap.get(kpMapKey)?.get('main_line');
  if (!kps) return null;

  const lastIdx = kps.length - 1;
  let idx = kpBinSearch(kps, kpCalc0);
  while (true) {
    const nextIdx = moveIndexFunc(idx);
    if (nextIdx < 0 || nextIdx > lastIdx) { break; }
    idx = nextIdx;
    const cmpKpObj = kps[idx];
    if (Math.abs(kpCalc0 - cmpKpObj.kpCalc) > distance) {
      break;
    }
  }
  return kps[idx];
};
// routesの始点側と終点側に付け足すKPの長さ
// (路線全体だとさすがに長くなりすぎるので)
const getStartKpOfRoute = (routeObj: RouteSearchRouteEntry) => {
  const isKpInc = isKpIncreaseDirection(
    routeObj.road_name,
    routeObj.direction,
  );
  const moveIndexFunc = isKpInc ? (i: number) => i - 1 : (i: number) => i + 1;
  const kpObj = findFirstKpOutsideDistance(
    routeObj.road_name,
    routeObj.direction,
    routeObj.kp_calc_end,
    moveIndexFunc,
    routeMarginDistance,
  );
  return kpObj?.kpCalc;
};
const getEndKpOfRoute = (routeObj: RouteSearchRouteEntry) => {
  const isKpInc = isKpIncreaseDirection(
    routeObj.road_name,
    routeObj.direction,
  );
  const moveIndexFunc = isKpInc ? (i: number) => i + 1 : (i: number) => i - 1;
  const kpObj = findFirstKpOutsideDistance(
    routeObj.road_name,
    routeObj.direction,
    routeObj.kp_calc_start,
    moveIndexFunc,
    routeMarginDistance,
  );
  return kpObj?.kpCalc;
};
const fillStartSideRouteInfo = (routeEntries: RouteSearchRouteEntry[]) => {
  // routesの先頭要素にkp_calc_startが入ってないので入れる.
  // 場合によっては先頭側に新たな要素を追加する必要もあるが、
  // (roadNameの前側に同じroadNameDispで異なるroadNameがあるとか)
  // 今は省略する.
  const firstRoute = routeEntries[0];
  firstRoute.kp_calc_start = getStartKpOfRoute(firstRoute) || 0;
};
const fillEndSideRouteInto = (routeEntries: RouteSearchRouteEntry[]) => {
  // routesの末尾要素にkp_calc_endが入ってないので入れる.
  // あと、fillStartSideRouteInfoと同様に省略.
  const lastRoute = routeEntries[routeEntries.length - 1];
  lastRoute.kp_calc_end = getEndKpOfRoute(lastRoute) || 0;
};
const doRouteSearch = () => {
  const routeEntries: RouteSearchRouteEntry[] = [];
  let currentRouteBlock: RouteSearchRouteEntry | null = null;
  for (const route of state.routes) {
    if (!isRowFilled(route)) return;

    const routeInfoMap = route.selectedRoadNameDispObj?.routeInfoMap;
    // 最初と最後はあとで詰める
    if (!routeInfoMap) { continue; }

    const routeInfo = routeInfoMap[route.selectedDirection];
    routeInfo.forEach(e => {
      if (!currentRouteBlock) {
        // 初回
        currentRouteBlock = {
          road_name: e.roadName,
          direction: e.direction,
          place_name: e.placeName,
          kp_calc_start: 0, // dummy
          kp_calc_end: 0, // dummy
        };
      }
      if (e.type === 'start') {
        currentRouteBlock = {
          road_name: e.roadName,
          direction: e.direction,
          place_name: e.placeName,
          kp_calc_start: e.kpStart || 0,
          kp_calc_end: 0, // dummy
        };
      } else {
        currentRouteBlock.kp_calc_end = e.kpEnd || 0;
        routeEntries.push(currentRouteBlock);
      }
    });
  }
  if (currentRouteBlock) {
    routeEntries.push(currentRouteBlock);
  }

  // 先頭側のルート情報を追加
  fillStartSideRouteInfo(routeEntries);
  // 末尾のルート情報を追加
  fillEndSideRouteInto(routeEntries);

  emit('route-search', { data: routeEntries });
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[8] || (_cache[8] = _createStaticVNode("<div class=\"route-element\"><div class=\"road-name-direction\"><div class=\"buttons ib vm\"></div><div class=\"road-name ib vm mr4\"><label class=\"form-control-label\">路線名</label></div><div class=\"direction ib vm\"><label class=\"form-control-label\">方向</label></div><div class=\"buttons ib vm ml1\"></div></div></div>", 1)),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(state.routes, (route, i) => {
      return (_openBlock(), _createElementBlock("div", {
        class: "route-element",
        key: i
      }, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            (i < maxLen - 1 && isLast(i))
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: "add-button",
                  onClick: ($event: any) => (addNextRow(route)),
                  disabled: !isRowFilled(route)
                }, [...(_cache[1] || (_cache[1] = [
                  _createElementVNode("i", { class: "fas fa-plus-circle" }, null, -1)
                ]))], 8, _hoisted_4))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("div", _hoisted_5, [
            _withDirectives(_createElementVNode("select", {
              class: "custom-select",
              "onUpdate:modelValue": ($event: any) => ((route.selectedRoadNameDispObj) = $event),
              onChange: ($event: any) => (onRoadNameDispChange(i))
            }, [
              _cache[2] || (_cache[2] = _createElementVNode("option", { value: null }, null, -1)),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(route.roadNameDisps, (e) => {
                return (_openBlock(), _createElementBlock("option", {
                  key: e.roadNameDisp,
                  value: e
                }, _toDisplayString(e.roadNameDispShort), 9, _hoisted_7))
              }), 128))
            ], 40, _hoisted_6), [
              [_vModelSelect, route.selectedRoadNameDispObj]
            ])
          ]),
          _createElementVNode("div", _hoisted_8, [
            _withDirectives(_createElementVNode("select", {
              class: "custom-select",
              "onUpdate:modelValue": ($event: any) => ((route.selectedDirection) = $event),
              onChange: ($event: any) => (onDirectionChange(i))
            }, [
              _cache[3] || (_cache[3] = _createElementVNode("option", { value: '' }, null, -1)),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList((route.selectedRoadNameDispObj || {}).directions, (e) => {
                return (_openBlock(), _createElementBlock("option", {
                  key: e,
                  value: e
                }, _toDisplayString(e), 9, _hoisted_10))
              }), 128))
            ], 40, _hoisted_9), [
              [_vModelSelect, route.selectedDirection]
            ])
          ]),
          _createElementVNode("div", _hoisted_11, [
            (i > 1 && isLast(i))
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: "del-button",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (deleteLastRow()))
                }, [...(_cache[4] || (_cache[4] = [
                  _createElementVNode("i", { class: "far fa-times-circle" }, null, -1)
                ]))]))
              : _createCommentVNode("", true)
          ])
        ]),
        _createElementVNode("div", _hoisted_12, [
          (!isLast(i))
            ? (_openBlock(), _createElementBlock("i", _hoisted_13))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                _cache[5] || (_cache[5] = _createElementVNode("i", { class: "arrow arrow-bulk fas fa-caret-down" }, null, -1)),
                _cache[6] || (_cache[6] = _createElementVNode("i", { class: "arrow arrow-bulk fas fa-caret-down" }, null, -1)),
                _cache[7] || (_cache[7] = _createElementVNode("i", { class: "arrow arrow-bulk fas fa-caret-down" }, null, -1))
              ], 64))
        ])
      ]))
    }), 128)),
    _createElementVNode("div", _hoisted_14, [
      _createElementVNode("button", {
        class: "btn btn-primary",
        disabled: !canRouteSearch.value,
        onClick: doRouteSearch
      }, " ルート検索 ", 8, _hoisted_15)
    ])
  ]))
}
}

})